document.documentElement.classList.remove("no-js");
document.documentElement.classList.add("js");

const closeMobileMenu = (menu, restoreFocus = false) => {
  if (!menu) return;
  const toggle = menu.querySelector("[data-menu-toggle]");
  const panel = menu.querySelector("[data-mobile-menu-panel]");
  menu.classList.remove("is-open");
  if (toggle) toggle.setAttribute("aria-expanded", "false");
  if (panel) panel.hidden = true;
  document.body.classList.remove("menu-open");
  if (restoreFocus) toggle?.focus();
};

const openMobileMenu = (menu) => {
  if (!menu) return;
  const toggle = menu.querySelector("[data-menu-toggle]");
  const panel = menu.querySelector("[data-mobile-menu-panel]");
  menu.classList.add("is-open");
  if (toggle) toggle.setAttribute("aria-expanded", "true");
  if (panel) panel.hidden = false;
  document.body.classList.add("menu-open");
};

const initializeMobileMenus = (root = document) => {
  root.querySelectorAll("[data-mobile-menu]").forEach((menu) => {
    if (menu.dataset.menuInitialized === "true") return;
    menu.dataset.menuInitialized = "true";
    const toggle = menu.querySelector("[data-menu-toggle]");
    toggle?.addEventListener("click", () => {
      if (menu.classList.contains("is-open")) closeMobileMenu(menu, true);
      else openMobileMenu(menu);
    });
  });
};

const initializeHeroCarousels = (root = document) => {
  const selector = "[data-hero-carousel]";
  const carousels = root.matches?.(selector) ? [root] : [];
  carousels.push(...root.querySelectorAll(selector));

  carousels.forEach((carousel) => {
    if (carousel.dataset.carouselInitialized === "true") return;

    const slides = [...carousel.querySelectorAll("[data-hero-carousel-slide]")];
    const controls = carousel.querySelector("[data-hero-carousel-controls]");
    const dots = [...carousel.querySelectorAll("[data-hero-carousel-dot]")];
    const status = carousel.querySelector("[data-hero-carousel-status]");
    if (slides.length < 2 || !controls || dots.length !== slides.length) return;

    carousel.dataset.carouselInitialized = "true";
    carousel.dataset.carouselReady = "true";
    controls.hidden = false;
    slides.forEach((slide) => { slide.hidden = false; });

    let activeIndex = 0;
    let touchStartX = null;
    let touchStartY = null;

    const showSlide = (requestedIndex, announce = true) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      carousel.dataset.carouselIndex = String(activeIndex);

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", String(!isActive));
        slide.inert = !isActive;
      });

      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle("is-active", isActive);
        if (isActive) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });

      if (announce && status) {
        const title = slides[activeIndex].dataset.carouselTitle || `slide ${activeIndex + 1}`;
        status.textContent = `${title}, slide ${activeIndex + 1} of ${slides.length}`;
      }
    };

    dots.forEach((dot) => {
      dot.addEventListener("click", () => showSlide(Number(dot.dataset.slideIndex || 0)));
    });

    carousel.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      showSlide(activeIndex + (event.key === "ArrowRight" ? 1 : -1));
    });

    carousel.addEventListener("touchstart", (event) => {
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });

    carousel.addEventListener("touchend", (event) => {
      if (touchStartX == null || touchStartY == null || event.changedTouches.length !== 1) return;
      const distanceX = event.changedTouches[0].clientX - touchStartX;
      const distanceY = event.changedTouches[0].clientY - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(distanceX) < 48 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
      showSlide(activeIndex + (distanceX < 0 ? 1 : -1));
    }, { passive: true });

    showSlide(0, false);
  });
};

const initializeDropNotes = (root = document) => {
  const selector = "[data-drop-notes]";
  const sections = root.matches?.(selector) ? [root] : [];
  sections.push(...root.querySelectorAll(selector));

  sections.forEach((section) => {
    const form = section.querySelector(".drop-notes-card__form");
    const emailInput = section.querySelector("[data-drop-notes-email]");
    const result = section.querySelector("[data-drop-notes-result]");
    const validation = section.querySelector("[data-drop-notes-validation]");
    const recoverable = section.querySelector("[data-drop-notes-recoverable]");
    const submit = section.querySelector("[data-drop-notes-submit]");
    const submitLabel = section.querySelector("[data-drop-notes-submit-label]");
    const defaultSubmitLabel = submitLabel?.textContent;

    if (["success", "already-subscribed"].includes(result?.dataset.dropNotesResultKind)) {
      window.requestAnimationFrame(() => result.focus());
    }
    if (validation && !validation.hidden && emailInput) {
      window.requestAnimationFrame(() => emailInput.focus());
    }

    if (!form || !emailInput || form.dataset.dropNotesInitialized === "true") return;
    form.dataset.dropNotesInitialized = "true";
    form.noValidate = true;

    const showValidation = () => {
      if (!validation) return;
      validation.hidden = false;
      emailInput.setAttribute("aria-invalid", "true");
      emailInput.setAttribute("aria-describedby", validation.id);
      emailInput.focus();
    };

    const clearValidation = () => {
      if (validation) validation.hidden = true;
      emailInput.removeAttribute("aria-invalid");
      emailInput.removeAttribute("aria-describedby");
    };

    const showRecoverable = () => {
      if (!recoverable) return;
      recoverable.hidden = false;
      recoverable.setAttribute("data-drop-notes-result", "");
      recoverable.removeAttribute("data-drop-notes-result-pending");
      if (submitLabel) submitLabel.textContent = "try again";
    };

    const returnedFormId = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    const returnedWithoutKnownResult = new URLSearchParams(window.location.search).get("form_type") === "customer"
      && returnedFormId === form.id
      && !result;
    if (returnedWithoutKnownResult) showRecoverable();

    emailInput.addEventListener("input", clearValidation);

    form.addEventListener("submit", (event) => {
      if (form.dataset.dropNotesSubmitting === "true") {
        event.preventDefault();
        return;
      }

      if (!emailInput.checkValidity() || !emailInput.value.trim()) {
        event.preventDefault();
        showValidation();
        return;
      }

      clearValidation();
      form.dataset.dropNotesSubmitting = "true";
      form.setAttribute("aria-busy", "true");
      if (submit) {
        submit.disabled = true;
        submit.setAttribute("aria-disabled", "true");
      }
      if (submitLabel) submitLabel.textContent = "joining…";
    });

    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return;
      delete form.dataset.dropNotesSubmitting;
      form.removeAttribute("aria-busy");
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute("aria-disabled");
      }
      if (submitLabel && defaultSubmitLabel) submitLabel.textContent = defaultSubmitLabel;
    });
  });
};

const shouldUseStaticGiftingMascot = () => {
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const constrainedConnection = connection?.saveData === true
    || ["slow-2g", "2g", "3g"].includes(connection?.effectiveType);

  return reducedMotion || constrainedConnection;
};

const initializeGiftingMascotAnimations = (root = document) => {
  const selector = "[data-gifting-mascot-animation]";
  const images = root.matches?.(selector) ? [root] : [];
  images.push(...root.querySelectorAll(selector));

  images.forEach((image) => {
    if (image.dataset.giftingMascotInitialized === "true") return;
    image.dataset.giftingMascotInitialized = "true";

    const staticSource = image.dataset.staticSrc;
    const animatedSource = image.dataset.animatedSrc;
    if (!staticSource || !animatedSource || shouldUseStaticGiftingMascot()) {
      image.dataset.animationState = "static";
      return;
    }

    image.addEventListener("error", () => {
      if (image.dataset.animationState !== "loading") return;
      image.dataset.animationState = "fallback";
      image.src = staticSource;
    }, { once: true });
    image.addEventListener("load", () => {
      if (image.dataset.animationState === "loading") image.dataset.animationState = "animated";
    }, { once: true });

    image.dataset.animationState = "loading";
    image.src = animatedSource;
  });
};

const synchronizePrivacyContact = () => {
  const contactPrefix = "Should you have any questions about our privacy practices or this Privacy Policy";
  const contactCopy = `${contactPrefix}, or if you would like to exercise any of the rights available to you, please call or email us at hello@blysandbeary.com`;
  const isPrivacyPolicy = window.location.pathname.includes("/policies/privacy-policy");
  const isContactPage = Boolean(document.querySelector(".contact-page"));
  if (!isPrivacyPolicy && !isContactPage) return;

  const normalizeText = (element) => element.textContent.replace(/\s+/g, " ").trim();

  if (isContactPage) {
    document.querySelectorAll(".contact-page__intro > .rte p").forEach((paragraph) => {
      if (normalizeText(paragraph).startsWith(contactPrefix)) paragraph.remove();
    });
  }

  if (!isPrivacyPolicy) return;
  const policyBody = document.querySelector(".shopify-policy__body, .template-policy .rte");
  if (!policyBody) return;

  let hasCurrentContact = false;
  policyBody.querySelectorAll("p").forEach((paragraph) => {
    const text = normalizeText(paragraph);
    if (!text.startsWith(contactPrefix)) return;
    if (text.includes("hello@blysandbeary.com")) hasCurrentContact = true;
    else paragraph.remove();
  });

  if (hasCurrentContact) return;
  const contactHeading = [...policyBody.querySelectorAll("h2, h3, h4")].find(
    (heading) => normalizeText(heading).toLowerCase() === "contact"
  );
  if (!contactHeading) return;

  const paragraph = document.createElement("p");
  const email = document.createElement("a");
  const copyBeforeEmail = contactCopy.replace("hello@blysandbeary.com", "");
  email.href = "mailto:hello@blysandbeary.com";
  email.textContent = "hello@blysandbeary.com";
  paragraph.append(document.createTextNode(copyBeforeEmail), email);
  contactHeading.insertAdjacentElement("afterend", paragraph);
};

const updateGalleryImage = (scope, source, alt) => {
  if (!scope || !source) return;
  const mainImage = scope.querySelector("[data-gallery-main]");
  if (!mainImage) return;

  mainImage.removeAttribute("srcset");
  mainImage.removeAttribute("sizes");
  mainImage.src = source;
  if (alt) mainImage.alt = alt;
};

const setGalleryPosition = (gallery, requestedPosition) => {
  if (!gallery) return;
  const thumbnails = [...gallery.querySelectorAll("[data-gallery-thumb]")];
  if (!thumbnails.length) return;

  const position = (requestedPosition + thumbnails.length) % thumbnails.length;
  const selectedThumbnail = thumbnails[position];
  updateGalleryImage(gallery, selectedThumbnail.dataset.imageSrc, selectedThumbnail.dataset.imageAlt);
  gallery.dataset.galleryIndex = String(position);

  thumbnails.forEach((thumbnail, index) => {
    const isActive = index === position;
    thumbnail.classList.toggle("is-active", isActive);
    thumbnail.setAttribute("aria-pressed", String(isActive));
  });

  const count = gallery.querySelector("[data-gallery-count-text]");
  if (count) count.textContent = `${position + 1} / ${thumbnails.length}`;
};

const normalizeGalleryImageSource = (source) => {
  if (!source) return "";

  try {
    const url = new URL(source, window.location.href);
    url.searchParams.delete("width");
    return `${url.origin}${url.pathname}?${url.searchParams.toString()}`.replace(/\?$/, "");
  } catch (_error) {
    return source;
  }
};

const showVariantGalleryImage = (scope, selectedOption) => {
  if (!scope || !selectedOption) return;

  const imageSource = selectedOption.dataset.imageSrc;
  if (!imageSource) return;

  const gallery = scope.querySelector("[data-product-gallery]");
  const thumbnails = [...(gallery?.querySelectorAll("[data-gallery-thumb]") || [])];
  const mediaId = selectedOption.dataset.mediaId;
  let matchingPosition = -1;

  if (mediaId) {
    matchingPosition = thumbnails.findIndex((thumbnail) => thumbnail.dataset.mediaId === mediaId);
  }

  if (matchingPosition < 0) {
    const normalizedSource = normalizeGalleryImageSource(imageSource);
    matchingPosition = thumbnails.findIndex(
      (thumbnail) => normalizeGalleryImageSource(thumbnail.dataset.imageSrc) === normalizedSource
    );
  }

  if (gallery && matchingPosition >= 0) {
    setGalleryPosition(gallery, matchingPosition);
    return;
  }

  updateGalleryImage(scope, imageSource, selectedOption.dataset.imageAlt);
};

const initializeProductGalleries = (root = document) => {
  root.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
    if (gallery.dataset.galleryInitialized === "true") return;
    gallery.dataset.galleryInitialized = "true";

    const main = gallery.querySelector(".preorder-gallery__main");
    if (!main || gallery.querySelectorAll("[data-gallery-thumb]").length < 2) return;

    let touchStartX = 0;
    main.addEventListener("touchstart", (event) => {
      touchStartX = event.changedTouches[0]?.clientX || 0;
    }, { passive: true });
    main.addEventListener("touchend", (event) => {
      const touchEndX = event.changedTouches[0]?.clientX || 0;
      const movement = touchEndX - touchStartX;
      if (Math.abs(movement) < 45) return;
      const currentPosition = Number(gallery.dataset.galleryIndex || 0);
      setGalleryPosition(gallery, currentPosition + (movement < 0 ? 1 : -1));
    }, { passive: true });
  });
};

const getQuantityElements = (formShell) => ({
  input: formShell?.querySelector("[data-quantity-input]"),
  minus: formShell?.querySelector("[data-quantity-minus]"),
  plus: formShell?.querySelector("[data-quantity-plus]"),
  message: formShell?.querySelector("[data-quantity-message]")
});

const synchronizeStickyQuantity = (scope, value, minimum, maximum) => {
  const bar = scope?.querySelector("[data-mobile-purchase-bar]");
  if (!bar) return;
  const output = bar.querySelector("[data-sticky-quantity]");
  const minus = bar.querySelector("[data-sticky-quantity-minus]");
  const plus = bar.querySelector("[data-sticky-quantity-plus]");
  const message = bar.querySelector("[data-sticky-quantity-message]");
  if (output) output.textContent = String(value);
  if (minus) minus.disabled = value <= minimum;
  if (plus) plus.disabled = maximum != null && value >= maximum;
  if (message) {
    const formShell = scope.querySelector("[data-product-form-shell]");
    message.textContent = formShell && maximum != null && maximum >= minimum && value >= maximum
      ? getQuantityLimitMessage(formShell, maximum)
      : "";
  }
};

const getQuantityLimitMessage = (formShell, maximum) => {
  if (maximum === 1) return formShell.dataset.quantityLimitOne || "only 1 remaining item is available for this pre-order batch.";
  const template = formShell.dataset.quantityLimitMany || "only [quantity] remaining items are available for this pre-order batch.";
  return template.replace("[quantity]", String(maximum));
};

const setQuantity = (formShell, requestedValue) => {
  const { input, minus, plus, message } = getQuantityElements(formShell);
  if (!input) return 1;

  const minimum = Number(input.min || 1);
  const parsedMaximum = Number(input.max);
  const maximum = input.hasAttribute("max") && Number.isFinite(parsedMaximum) ? parsedMaximum : null;
  const value = Math.max(minimum, maximum == null ? requestedValue : Math.min(requestedValue, maximum));
  const canOrder = !formShell.querySelector("[data-add-to-cart]")?.disabled;
  input.value = String(value);
  if (minus) minus.disabled = !canOrder || value <= minimum;
  if (plus) plus.disabled = !canOrder || (maximum != null && value >= maximum);
  if (message) {
    message.textContent = maximum != null && maximum >= minimum && value >= maximum
      ? getQuantityLimitMessage(formShell, maximum)
      : "";
  }

  const scope = formShell.closest("[data-product-scope], .featured-drop");
  synchronizeStickyQuantity(scope, value, minimum, maximum);
  return value;
};

const updateQuantityMaximum = (formShell, selectedOption) => {
  const { input } = getQuantityElements(formShell);
  if (!input) return true;

  let maximum = null;
  if (
    selectedOption?.dataset.inventoryManaged === "true" &&
    selectedOption.dataset.inventoryPolicy === "deny"
  ) {
    maximum = Math.max(0, Number(selectedOption.dataset.inventoryQuantity || 0));
  }

  const preorderLimit = Number(formShell.dataset.preorderLimit || 0);
  if (preorderLimit > 0) maximum = maximum == null ? preorderLimit : Math.min(maximum, preorderLimit);

  if (maximum == null) input.removeAttribute("max");
  else input.max = String(maximum);

  const minimum = Number(input.min || 1);
  setQuantity(formShell, Number(input.value || minimum));
  return maximum == null || maximum >= minimum;
};

const getMultiVariantInputs = (formShell) => (
  [...(formShell?.querySelectorAll("[data-multi-quantity-input]") || [])]
);

const getMultiVariantTotal = (formShell) => (
  getMultiVariantInputs(formShell).reduce((total, input) => total + Number(input.value || 0), 0)
);

const getMultiVariantMaximum = (formShell, input) => {
  const currentValue = Number(input.value || 0);
  const parsedMaximum = Number(input.max);
  let maximum = input.hasAttribute("max") && Number.isFinite(parsedMaximum) ? parsedMaximum : null;
  const preorderLimit = Number(formShell.dataset.preorderLimit || 0);

  if (preorderLimit > 0) {
    const otherQuantity = getMultiVariantTotal(formShell) - currentValue;
    const remainingForVariant = Math.max(0, preorderLimit - otherQuantity);
    maximum = maximum == null ? remainingForVariant : Math.min(maximum, remainingForVariant);
  }

  return maximum;
};

const updateMultiVariantForm = (formShell, requestedMessage = "") => {
  const form = formShell?.querySelector("[data-multi-variant-form]");
  if (!form) return;

  const scope = formShell.closest("[data-product-scope], .featured-drop");
  const dropState = getDropState(scope);
  const inputs = getMultiVariantInputs(formShell);
  const availableInputs = inputs.filter((input) => input.dataset.available === "true");
  const total = getMultiVariantTotal(formShell);
  const canChoose = dropState === "open" && availableInputs.length > 0;
  const canOrder = canChoose && total > 0;
  const addButton = formShell.querySelector("[data-add-to-cart]");
  const buttonText = formShell.querySelector("[data-add-to-cart-text]");
  const status = formShell.querySelector("[data-product-status]");

  inputs.forEach((input) => {
    const stepper = input.closest("[data-multi-quantity-stepper]");
    const minus = stepper?.querySelector("[data-multi-quantity-minus]");
    const plus = stepper?.querySelector("[data-multi-quantity-plus]");
    const value = Number(input.value || 0);
    const maximum = getMultiVariantMaximum(formShell, input);
    const available = input.dataset.available === "true";
    if (minus) minus.disabled = !canChoose || !available || value <= 0;
    if (plus) plus.disabled = !canChoose || !available || (maximum != null && value >= maximum);
  });

  if (addButton) addButton.disabled = !canChoose || form.dataset.submitting === "true";
  if (buttonText) {
    if (form.dataset.submitting === "true") buttonText.textContent = formShell.dataset.addingLabel || "adding to cart…";
    else if (dropState === "before") buttonText.textContent = formShell.dataset.beforeLabel;
    else if (dropState === "open" && availableInputs.length > 0) buttonText.textContent = formShell.dataset.openLabel;
    else buttonText.textContent = formShell.dataset.fullLabel;
  }

  if (status) {
    if (dropState === "before") status.textContent = formShell.dataset.beforeMessage;
    else if (dropState === "full" || availableInputs.length === 0) status.textContent = formShell.dataset.fullMessage;
    else status.textContent = requestedMessage;
  }

  formShell.dataset.canOrder = String(canOrder);

  const summary = scope?.querySelector("[data-sticky-selection-summary]");
  if (summary) {
    if (total === 0) summary.textContent = "choose quantities";
    else {
      const unit = total === 1
        ? (formShell.dataset.multiUnitSingular || "item")
        : (formShell.dataset.multiUnitPlural || "items");
      summary.textContent = `${total} ${unit} selected`;
    }
  }

  updateMobilePurchaseBar(scope);
};

const setMultiVariantQuantity = (input, requestedValue) => {
  const formShell = input?.closest("[data-product-form-shell]");
  if (!formShell || input.dataset.available !== "true") return 0;

  const scope = formShell.closest("[data-product-scope], .featured-drop");
  if (getDropState(scope) !== "open") return Number(input.value || 0);

  const previousValue = Number(input.value || 0);
  const maximum = getMultiVariantMaximum(formShell, input);
  const value = Math.max(0, maximum == null ? requestedValue : Math.min(requestedValue, maximum));
  const reachedLimit = maximum != null && requestedValue > maximum;
  input.value = String(value);

  const message = reachedLimit ? getQuantityLimitMessage(formShell, maximum) : "";
  updateMultiVariantForm(formShell, message);

  if (value > previousValue) {
    showVariantGalleryImage(scope, input);
    const variantId = input.dataset.variantId;
    if (variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variantId);
      window.history.replaceState({}, "", url);
    }
  }

  return value;
};

const promptMultiVariantSelection = (formShell) => {
  if (!formShell?.querySelector("[data-multi-variant-form]") || getMultiVariantTotal(formShell) > 0) return false;
  const status = formShell.querySelector("[data-product-status]");
  if (status) status.textContent = formShell.dataset.multiSelectionMessage || "choose a quantity for at least one flavour";
  const firstAvailable = getMultiVariantInputs(formShell).find((input) => input.dataset.available === "true");
  firstAvailable?.closest("[data-multi-quantity-stepper]")?.querySelector("[data-multi-quantity-plus]")?.focus();
  return true;
};

const prepareWeeklyDropCartSwitch = async (formShell, items) => {
  if (!window.BBCartSwitcher?.prepareWeeklyDropAdd) return { handled: false };
  return window.BBCartSwitcher.prepareWeeklyDropAdd({
    formShell,
    targetLabel: formShell.dataset.productTitle || "this week’s drop",
    items
  });
};

const clearSavedWeeklyDropChoices = () => {
  window.BBCartSwitcher?.clearWeeklyDropDraft?.();
};

const savedWeeklyDropChoices = (formShell) => {
  if (!window.BBCartSwitcher?.weeklyDraftForProduct) return [];
  return window.BBCartSwitcher.weeklyDraftForProduct(Number(formShell?.dataset.productId));
};

const showRestoredWeeklyDropChoices = (formShell) => {
  const status = formShell?.querySelector("[data-product-status]");
  if (!status || formShell.dataset.dropState !== "open") return;
  status.textContent = "your saved choices are ready to review. availability has been checked again.";
  status.hidden = false;
};

const resetSingleVariantForm = (formShell, requestedMessage = "") => {
  const form = formShell?.querySelector("form.product-form");
  const addButton = formShell?.querySelector("[data-add-to-cart]");
  const buttonText = formShell?.querySelector("[data-add-to-cart-text]");
  const status = formShell?.querySelector("[data-product-status]");
  if (form) {
    form.dataset.submitting = "false";
    form.removeAttribute("aria-busy");
  }
  if (addButton) addButton.disabled = false;
  if (buttonText) buttonText.textContent = formShell.dataset.openLabel || "pre-order now";
  if (status && requestedMessage) {
    status.textContent = requestedMessage;
    status.hidden = false;
  }
};

const singleVariantItems = (form) => {
  const formData = new FormData(form);
  const id = Number(formData.get("id"));
  const quantity = Math.max(1, Number(formData.get("quantity") || 1));
  if (!Number.isSafeInteger(id) || id < 1 || !Number.isSafeInteger(quantity)) return [];
  const properties = {};
  for (const [name, value] of formData.entries()) {
    const match = String(name).match(/^properties\[(.+)\]$/);
    if (match && String(value)) properties[match[1]] = String(value);
  }
  const sellingPlan = Number(formData.get("selling_plan"));
  return [{
    id,
    quantity,
    ...(Object.keys(properties).length ? { properties } : {}),
    ...(Number.isSafeInteger(sellingPlan) && sellingPlan > 0 ? { selling_plan: sellingPlan } : {})
  }];
};

const submitSingleVariantForm = async (formShell) => {
  const form = formShell?.querySelector("form.product-form");
  const addButton = formShell?.querySelector("[data-add-to-cart]");
  const buttonText = formShell?.querySelector("[data-add-to-cart-text]");
  const status = formShell?.querySelector("[data-product-status]");
  if (!form || !addButton || form.dataset.submitting === "true") return;

  const items = singleVariantItems(form);
  if (!items.length) {
    resetSingleVariantForm(formShell, "we couldn’t read that selection. please choose it again.");
    return;
  }

  form.dataset.submitting = "true";
  form.setAttribute("aria-busy", "true");
  addButton.disabled = true;
  if (buttonText) buttonText.textContent = formShell.dataset.addingLabel || "adding to cart…";

  try {
    const switchResult = await prepareWeeklyDropCartSwitch(formShell, items);
    if (switchResult.handled) {
      if (switchResult.status === "switched") {
        window.location.assign(formShell.dataset.cartUrl || `${window.Shopify?.routes?.root || "/"}cart`);
        return;
      }
      resetSingleVariantForm(formShell, switchResult.status === "failed" ? switchResult.message : "");
      return;
    }

    const response = await fetch(formShell.dataset.cartAddUrl || `${window.Shopify?.routes?.root || "/"}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.description || result.message || "we couldn’t add that to your cart");
    clearSavedWeeklyDropChoices();
    window.location.assign(formShell.dataset.cartUrl || `${window.Shopify?.routes?.root || "/"}cart`);
  } catch (error) {
    resetSingleVariantForm(formShell, error.message || "we couldn’t add that to your cart");
    status?.focus();
  }
};

const submitMultiVariantForm = async (formShell) => {
  const form = formShell?.querySelector("[data-multi-variant-form]");
  if (!form || form.dataset.submitting === "true" || promptMultiVariantSelection(formShell)) return;

  const items = getMultiVariantInputs(formShell)
    .map((input) => ({ id: Number(input.dataset.variantId), quantity: Number(input.value || 0) }))
    .filter((item) => Number.isFinite(item.id) && item.quantity > 0);
  if (!items.length) return;

  const status = formShell.querySelector("[data-product-status]");
  form.dataset.submitting = "true";
  form.setAttribute("aria-busy", "true");
  updateMultiVariantForm(formShell);

  try {
    const switchResult = await prepareWeeklyDropCartSwitch(formShell, items);
    if (switchResult.handled) {
      if (switchResult.status === "switched") {
        window.location.assign(formShell.dataset.cartUrl || `${window.Shopify?.routes?.root || "/"}cart`);
        return;
      }
      form.dataset.submitting = "false";
      form.removeAttribute("aria-busy");
      updateMultiVariantForm(formShell, switchResult.status === "failed" ? switchResult.message : "");
      return;
    }

    const response = await fetch(formShell.dataset.cartAddUrl || `${window.Shopify?.routes?.root || "/"}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ items })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.description || result.message || "we couldn’t add those quantities to your cart");
    clearSavedWeeklyDropChoices();

    if (formShell.dataset.cartUrl) window.location.assign(formShell.dataset.cartUrl);
    else {
      form.dataset.submitting = "false";
      form.removeAttribute("aria-busy");
      updateMultiVariantForm(formShell, "your loaves are in the cart");
    }
  } catch (error) {
    form.dataset.submitting = "false";
    form.removeAttribute("aria-busy");
    updateMultiVariantForm(formShell, error.message || "we couldn’t add those quantities to your cart");
    status?.focus();
  }
};

const initializeMultiVariantForms = (root = document) => {
  root.querySelectorAll("[data-product-form-shell]").forEach((formShell) => {
    if (!formShell.querySelector("[data-multi-variant-form]")) return;
    const savedLines = savedWeeklyDropChoices(formShell);
    if (savedLines.length) {
      const savedByVariant = new Map(savedLines.map((line) => [Number(line.variantId), Number(line.quantity)]));
      getMultiVariantInputs(formShell).forEach((input) => {
        const quantity = savedByVariant.get(Number(input.dataset.variantId));
        if (quantity) setMultiVariantQuantity(input, quantity);
      });
    }
    updateMultiVariantForm(formShell);
    if (savedLines.length) showRestoredWeeklyDropChoices(formShell);
  });
};

const updateMobilePurchaseBar = (scope) => {
  const bar = scope?.querySelector("[data-mobile-purchase-bar]");
  const formShell = scope?.querySelector("[data-product-form-shell]");
  const addButton = formShell?.querySelector("[data-add-to-cart]");
  if (!bar || !formShell || !addButton) return;

  const footer = document.querySelector(".site-footer");
  const nextDrop = document.querySelector("[data-drop-notes]");
  const visualViewport = window.visualViewport;
  const activeElement = document.activeElement;
  const activeElementIsEditable = Boolean(
    activeElement?.matches?.('input, textarea, select, [contenteditable="true"]')
  );
  const keyboardOpen = Boolean(
    activeElementIsEditable
    && visualViewport
    && visualViewport.height < window.innerHeight - 120
  );
  const formHasScrolledAbove = formShell.getBoundingClientRect().bottom < 0;
  const barHeight = bar.getBoundingClientRect().height || 80;
  const footerReached = footer && footer.getBoundingClientRect().top <= window.innerHeight - barHeight;
  const nextDropReached = nextDrop && nextDrop.getBoundingClientRect().top <= window.innerHeight;
  const mobile = window.matchMedia("(max-width: 749px)").matches;
  const shouldShow = mobile
    && formHasScrolledAbove
    && !footerReached
    && !nextDropReached
    && !keyboardOpen
    && !addButton.disabled;

  bar.hidden = !shouldShow;
  bar.setAttribute("aria-hidden", String(!shouldShow));
  document.body.classList.toggle(
    "mobile-purchase-bar-visible",
    Boolean(document.querySelector("[data-mobile-purchase-bar]:not([hidden])"))
  );
};

const initializeQuantitySteppers = (root = document) => {
  root.querySelectorAll("[data-product-form-shell]").forEach((formShell) => {
    const { input } = getQuantityElements(formShell);
    if (!input) return;
    const currentVariantId = Number(formShell.querySelector('form.product-form input[name="id"]')?.value);
    const savedLine = savedWeeklyDropChoices(formShell).find((line) => Number(line.variantId) === currentVariantId);
    setQuantity(formShell, savedLine?.quantity || Number(input.value || input.min || 1));
    if (savedLine) showRestoredWeeklyDropChoices(formShell);
  });
};

const initializeMobilePurchaseBars = (root = document) => {
  root.querySelectorAll("[data-mobile-purchase-bar]").forEach((bar) => {
    if (bar.dataset.stickyInitialized === "true") return;
    bar.dataset.stickyInitialized = "true";
    const scope = bar.closest("[data-product-scope], .featured-drop");
    const refresh = () => updateMobilePurchaseBar(scope);
    window.addEventListener("scroll", refresh, { passive: true });
    window.addEventListener("resize", refresh);
    window.addEventListener("pageshow", refresh);
    document.addEventListener("visibilitychange", refresh);
    window.visualViewport?.addEventListener("resize", refresh);
    refresh();
  });
};

const closeVariantPicker = (picker, restoreFocus = false) => {
  if (!picker) return;
  const button = picker.querySelector("[data-variant-picker-button]");
  const listbox = picker.querySelector("[data-variant-picker-listbox]");
  if (!button || !listbox) return;
  button.setAttribute("aria-expanded", "false");
  listbox.hidden = true;
  picker.classList.remove("is-open");
  if (restoreFocus) button.focus();
};

const getEnabledVariantOptions = (picker) => (
  [...picker.querySelectorAll("[data-variant-picker-option]")]
    .filter((option) => option.getAttribute("aria-disabled") !== "true")
);

const focusVariantOption = (picker, requestedIndex) => {
  const options = getEnabledVariantOptions(picker);
  if (!options.length) return;
  const index = (requestedIndex + options.length) % options.length;
  options[index].focus();
};

const openVariantPicker = (picker, direction = "selected") => {
  if (!picker) return;
  document.querySelectorAll("[data-variant-picker].is-open").forEach((openPicker) => {
    if (openPicker !== picker) closeVariantPicker(openPicker);
  });

  const button = picker.querySelector("[data-variant-picker-button]");
  const listbox = picker.querySelector("[data-variant-picker-listbox]");
  const options = getEnabledVariantOptions(picker);
  if (!button || !listbox) return;

  button.setAttribute("aria-expanded", "true");
  listbox.hidden = false;
  picker.classList.add("is-open");

  let target = options.find((option) => option.getAttribute("aria-selected") === "true");
  if (direction === "first") target = options[0];
  if (direction === "last") target = options[options.length - 1];
  if (target || options[0]) (target || options[0]).focus();
  else listbox.focus();
};

const synchronizeVariantPicker = (variantSelect) => {
  const picker = variantSelect?.closest("[data-variant-picker]");
  const buttonText = picker?.querySelector("[data-variant-picker-button-text]");
  const selectedOption = variantSelect?.options[variantSelect.selectedIndex];
  if (!picker || !buttonText || !selectedOption) return;

  buttonText.textContent = selectedOption.textContent.trim();
  picker.querySelectorAll("[data-variant-picker-option]").forEach((option) => {
    option.setAttribute("aria-selected", String(option.dataset.value === variantSelect.value));
  });
};

const selectVariantPickerOption = (option) => {
  const picker = option?.closest("[data-variant-picker]");
  const variantSelect = picker?.querySelector("[data-variant-select]");
  if (!picker || !variantSelect || option.getAttribute("aria-disabled") === "true") return;

  variantSelect.value = option.dataset.value;
  variantSelect.dispatchEvent(new Event("change", { bubbles: true }));
  closeVariantPicker(picker, true);
};

const initializeVariantPickers = (root = document) => {
  root.querySelectorAll("[data-variant-picker]").forEach((picker) => {
    if (picker.dataset.variantPickerInitialized === "true") return;
    const variantSelect = picker.querySelector("[data-variant-select]");
    if (!variantSelect) return;

    picker.dataset.variantPickerInitialized = "true";
    const instanceId = variantSelect.id || `Variant-${Math.random().toString(36).slice(2)}`;
    const labelId = picker.dataset.labelId;
    const buttonId = `${instanceId}-Button`;
    const buttonTextId = `${instanceId}-ButtonText`;
    const listboxId = `${instanceId}-Listbox`;

    const button = document.createElement("button");
    button.type = "button";
    button.id = buttonId;
    button.className = "variant-picker__button";
    button.setAttribute("data-variant-picker-button", "");
    button.setAttribute("aria-haspopup", "listbox");
    button.setAttribute("aria-expanded", "false");
    button.setAttribute("aria-controls", listboxId);
    button.setAttribute("aria-labelledby", labelId);
    button.setAttribute("aria-describedby", buttonTextId);
    button.innerHTML = `<span id="${buttonTextId}" class="variant-picker__button-text" data-variant-picker-button-text></span><svg class="variant-picker__chevron" viewBox="0 0 12 8" aria-hidden="true" focusable="false"><path d="m1 1 5 5 5-5" /></svg>`;

    const listbox = document.createElement("div");
    listbox.id = listboxId;
    listbox.className = "variant-picker__listbox";
    listbox.setAttribute("data-variant-picker-listbox", "");
    listbox.setAttribute("role", "listbox");
    listbox.setAttribute("aria-labelledby", labelId);
    listbox.setAttribute("tabindex", "-1");
    listbox.hidden = true;

    [...variantSelect.options].forEach((nativeOption) => {
      if (nativeOption.dataset.placeholder === "true") return;
      const option = document.createElement("button");
      option.type = "button";
      option.className = "variant-picker__option";
      option.textContent = nativeOption.textContent.trim();
      option.dataset.value = nativeOption.value;
      option.setAttribute("data-variant-picker-option", "");
      option.setAttribute("role", "option");
      option.setAttribute("tabindex", "-1");
      option.setAttribute("aria-selected", String(nativeOption.selected));
      option.setAttribute("aria-disabled", String(nativeOption.disabled));
      listbox.append(option);
    });

    picker.append(button, listbox);
    picker.classList.add("is-enhanced");
    variantSelect.setAttribute("tabindex", "-1");
    variantSelect.setAttribute("aria-hidden", "true");
    variantSelect.setAttribute("inert", "");
    document.getElementById(labelId)?.setAttribute("for", buttonId);
    synchronizeVariantPicker(variantSelect);

    button.addEventListener("click", () => {
      if (picker.classList.contains("is-open")) closeVariantPicker(picker, true);
      else openVariantPicker(picker);
    });
    button.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openVariantPicker(picker);
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        openVariantPicker(picker, "last");
      }
    });
    listbox.addEventListener("click", (event) => {
      const option = event.target.closest("[data-variant-picker-option]");
      if (option) selectVariantPickerOption(option);
    });
    listbox.addEventListener("keydown", (event) => {
      const activeOption = event.target.closest("[data-variant-picker-option]");
      const options = getEnabledVariantOptions(picker);
      const activeIndex = options.indexOf(activeOption);
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        focusVariantOption(picker, activeIndex + (event.key === "ArrowDown" ? 1 : -1));
      }
      if (event.key === "Home" || event.key === "End") {
        event.preventDefault();
        focusVariantOption(picker, event.key === "Home" ? 0 : options.length - 1);
      }
      if ((event.key === "Enter" || event.key === " ") && activeOption) {
        event.preventDefault();
        selectVariantPickerOption(activeOption);
      }
      if (event.key === "Escape") {
        event.preventDefault();
        closeVariantPicker(picker, true);
      }
      if (event.key === "Tab") closeVariantPicker(picker);
    });
  });
};

const updateVariantState = (variantSelect) => {
  const selectedOption = variantSelect.options[variantSelect.selectedIndex];
  const scope = variantSelect.closest("[data-product-scope], .featured-drop");
  const formShell = variantSelect.closest("[data-product-form-shell]");
  if (!selectedOption || !formShell) return;

  synchronizeVariantPicker(variantSelect);

  const hasSelectedVariant = selectedOption.dataset.placeholder !== "true" && variantSelect.value !== "";

  const price = scope?.querySelector("[data-product-price]");
  if (hasSelectedVariant && price && selectedOption.dataset.price) price.textContent = selectedOption.dataset.price;

  const dropState = getDropState(scope);
  formShell.dataset.dropState = dropState;
  const isAvailable = hasSelectedVariant && selectedOption.dataset.available === "true";
  const quantityAvailable = hasSelectedVariant ? updateQuantityMaximum(formShell, selectedOption) : false;
  const canOrder = dropState === "open" && hasSelectedVariant && isAvailable && quantityAvailable;
  const canChooseVariant = dropState === "open" && !hasSelectedVariant;
  const addButton = formShell.querySelector("[data-add-to-cart]");
  const buttonText = formShell.querySelector("[data-add-to-cart-text]");
  const status = formShell.querySelector("[data-product-status]");
  const quantityElements = getQuantityElements(formShell);

  if (addButton) {
    addButton.disabled = !canOrder && !canChooseVariant;
    addButton.classList.toggle("button--selection-required", canChooseVariant);
  }
  if (!canOrder) {
    if (quantityElements.minus) quantityElements.minus.disabled = true;
    if (quantityElements.plus) quantityElements.plus.disabled = true;
  } else {
    setQuantity(formShell, Number(quantityElements.input?.value || quantityElements.input?.min || 1));
  }
  if (buttonText) {
    if (dropState === "before") buttonText.textContent = formShell.dataset.beforeLabel;
    else if (dropState === "open" && (!hasSelectedVariant || canOrder)) buttonText.textContent = formShell.dataset.openLabel;
    else buttonText.textContent = formShell.dataset.fullLabel;
  }
  if (status) {
    if (dropState === "before") status.textContent = formShell.dataset.beforeMessage;
    else if (dropState === "full" || hasSelectedVariant && !canOrder) status.textContent = formShell.dataset.fullMessage;
    else status.textContent = "";
  }

  const dynamicCheckout = formShell.querySelector(".dynamic-checkout");
  if (dynamicCheckout) dynamicCheckout.hidden = !canOrder;

  formShell.dataset.canOrder = String(canOrder);

  const inventory = scope?.querySelector("[data-inventory-state]");
  const inventoryText = inventory?.querySelector("[data-inventory-state-text]");
  if (inventory && inventoryText) {
    const displayState = dropState === "open" && hasSelectedVariant && !isAvailable ? "full" : dropState;
    inventory.classList.remove("inventory-state--open", "inventory-state--before", "inventory-state--full");
    inventory.classList.add(`inventory-state--${displayState}`);
    if (displayState === "open") inventoryText.textContent = inventory.dataset.availableText;
    if (displayState === "before") inventoryText.textContent = inventory.dataset.beforeText;
    if (displayState === "full") inventoryText.textContent = inventory.dataset.fullText;
  }

  if (hasSelectedVariant) showVariantGalleryImage(scope, selectedOption);

  const stickyPrice = scope?.querySelector("[data-sticky-price]");
  if (hasSelectedVariant && stickyPrice && selectedOption.dataset.price) stickyPrice.textContent = selectedOption.dataset.price;
  updateMobilePurchaseBar(scope);

  if (hasSelectedVariant) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantSelect.value);
    window.history.replaceState({}, "", url);
  }
};

const promptVariantSelection = (formShell) => {
  const variantSelect = formShell?.querySelector("[data-variant-select]");
  if (!variantSelect || variantSelect.value) return false;

  const picker = variantSelect.closest("[data-variant-picker]");
  const status = formShell.querySelector("[data-product-status]");
  if (status) status.textContent = formShell.dataset.selectionMessage || "choose a flavour to continue";
  openVariantPicker(picker, "first");
  return true;
};

const getScheduledDropState = () => {
  if (document.body.dataset.dropAutoSchedule !== "true") return null;

  const opensAt = Date.parse(document.body.dataset.dropOpensAt || "");
  const closesAt = Date.parse(document.body.dataset.dropClosesAt || "");
  if (!Number.isFinite(opensAt) || !Number.isFinite(closesAt)) return null;

  const currentTime = Date.now();
  if (currentTime < opensAt) return "before";
  if (currentTime >= closesAt) return "closed";
  return "open";
};

const getDropState = (section) => {
  const renderedState = section?.dataset.dropState || document.body.dataset.globalDropState;
  const scheduledState = section?.dataset.dropSchedule === "manual" ? null : getScheduledDropState();
  if (!scheduledState) return renderedState;

  // Preserve the server-rendered fully reserved state during the scheduled order window.
  if (scheduledState === "open" && renderedState === "full") return "full";
  return scheduledState;
};

const scheduleDropStateRefresh = () => {
  if (document.body.dataset.dropAutoSchedule !== "true") return;

  const boundaries = [
    Date.parse(document.body.dataset.dropOpensAt || ""),
    Date.parse(document.body.dataset.dropClosesAt || "")
  ].filter((timestamp) => Number.isFinite(timestamp) && timestamp > Date.now());
  if (!boundaries.length) return;

  const nextBoundary = Math.min(...boundaries);
  const maximumTimeout = 2_147_000_000;
  const delay = Math.min(nextBoundary - Date.now() + 1_000, maximumTimeout);
  window.setTimeout(() => window.location.reload(), delay);
};

const expiredDropCartNoticeKey = "bb-expired-drop-cart-notice";
let expiredDropCartCleanupStarted = false;

const isGiftAddonCartItem = (item) => Boolean(item?.properties?._bb_gift_addon);
const isBigOrderCartItem = (item) => item?.properties?._bb_big_order === "true";

const getExpiredDropCartPlan = (cart, currentProductId, dropState) => {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const primaryItems = items.filter((item) => !isGiftAddonCartItem(item));
  const isValidBigOrderCart = cart?.attributes?.bb_order_mode === "big_order"
    && primaryItems.length > 0
    && primaryItems.every(isBigOrderCartItem);
  if (isValidBigOrderCart) return null;

  const expiredPrimaryItems = primaryItems.filter((item) => (
    Number(item.product_id) !== currentProductId
    || (Number(item.product_id) === currentProductId && dropState === "closed")
  ));
  if (!expiredPrimaryItems.length) return null;

  const expiredKeys = new Set(expiredPrimaryItems.map((item) => item.key));
  const hasCurrentPrimaryItem = primaryItems.some((item) => (
    !expiredKeys.has(item.key) && Number(item.product_id) === currentProductId
  ));
  const itemsToRemove = hasCurrentPrimaryItem
    ? expiredPrimaryItems
    : items.filter((item) => expiredKeys.has(item.key) || isGiftAddonCartItem(item));

  return {
    updates: Object.fromEntries(itemsToRemove.map((item) => [item.key, 0])),
    clearGiftDetails: !hasCurrentPrimaryItem
  };
};

const showExpiredDropCartNotice = () => {
  let message = "";
  try {
    message = window.sessionStorage.getItem(expiredDropCartNoticeKey) || "";
    window.sessionStorage.removeItem(expiredDropCartNoticeKey);
  } catch (_error) {
    return;
  }
  if (!message) return;

  const notice = document.querySelector("[data-expired-drop-cart-notice]");
  if (!notice) return;
  notice.textContent = message;
  notice.hidden = false;
};

const pruneExpiredDropCart = async () => {
  if (expiredDropCartCleanupStarted || Number(document.body.dataset.cartItemCount || 0) < 1) return;

  const currentProductId = Number(document.body.dataset.currentDropProductId);
  const cartUrl = document.body.dataset.cartUrl;
  const cartUpdateUrl = document.body.dataset.cartUpdateUrl;
  if (!Number.isFinite(currentProductId) || currentProductId < 1 || !cartUrl || !cartUpdateUrl) return;

  expiredDropCartCleanupStarted = true;
  try {
    const cartResponse = await fetch(cartUrl, { headers: { Accept: "application/json" } });
    if (!cartResponse.ok) throw new Error("cart could not be checked");
    const cart = await cartResponse.json();
    const plan = getExpiredDropCartPlan(cart, currentProductId, getDropState());
    if (!plan) return;

    const payload = { updates: plan.updates };
    if (plan.clearGiftDetails) {
      payload.attributes = { delivery_window: "", gift_card_requested: "", gift_message: "" };
    }
    const updateResponse = await fetch(cartUpdateUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(payload)
    });
    if (!updateResponse.ok) throw new Error("cart could not be updated");

    try {
      window.sessionStorage.setItem(
        expiredDropCartNoticeKey,
        "items from a past drop were removed from your bag"
      );
    } catch (_error) {
      // The cart is still corrected when session storage is unavailable.
    }
    window.location.reload();
  } catch (_error) {
    expiredDropCartCleanupStarted = false;
  }
};

const enforceInactiveDropButtons = (root = document) => {
  const selector = ".drop-hero, .featured-drop, .preorder-page";
  const sections = root.matches?.(selector) ? [root] : [];
  sections.push(...root.querySelectorAll(selector));

  sections.forEach((section) => {
    const dropState = getDropState(section);
    if (dropState !== "before" && dropState !== "full" && dropState !== "closed") return;

    const label = dropState === "before"
      ? section.dataset.beforeLabel || "pre-orders open soon"
      : dropState === "full"
        ? section.dataset.fullLabel || document.body.dataset.dropFullLabel || "fully reserved"
        : section.dataset.closedLabel || document.body.dataset.dropClosedLabel || "pre-orders closed";
    const hideClosedStatus = dropState === "closed" && section.dataset.hideClosedStatus === "true";
    const statusMessage = dropState === "full"
      ? section.dataset.fullMessage || document.body.dataset.dropFullMessage || "this week’s batch is fully reserved"
      : dropState === "closed"
        ? hideClosedStatus
          ? ""
          : section.dataset.closedMessage || document.body.dataset.dropClosedMessage || "this week’s pre-order window has closed"
        : "";
    section.querySelectorAll(".button:not([data-drop-state-ignore])").forEach((button) => {
      button.replaceChildren(document.createTextNode(label));
      button.classList.add("button--disabled");
      button.classList.toggle("button--coming-soon", dropState === "before");
      button.classList.toggle("button--full", dropState === "full");
      button.classList.toggle("button--closed", dropState === "closed");
      button.setAttribute("aria-disabled", "true");
      button.setAttribute("tabindex", "-1");

      if (button instanceof HTMLAnchorElement) button.removeAttribute("href");
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.type = "button";
      }
    });

    const status = section.querySelector("[data-product-status], [data-drop-status], .product-form__status");
    if (status && statusMessage) {
      status.textContent = statusMessage;
      status.hidden = false;
    } else if (status && hideClosedStatus) {
      status.textContent = "";
      status.hidden = true;
    }
  });
};

document.addEventListener(
  "click",
  (event) => {
    const dropButton = event.target.closest(
      ".drop-hero .button:not([data-drop-state-ignore]), .featured-drop .button:not([data-drop-state-ignore])"
    );
    if (!dropButton) return;

    const dropState = getDropState(dropButton.closest(".drop-hero, .featured-drop"));
    if (dropState !== "before" && dropState !== "full" && dropState !== "closed") return;

    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener(
  "submit",
  (event) => {
    const formShell = event.target.closest?.("[data-product-form-shell]");
    if (!formShell) return;

    const scope = formShell.closest("[data-product-scope], .featured-drop");
    if (event.target.matches("[data-multi-variant-form]")) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (getDropState(scope) === "open") submitMultiVariantForm(formShell);
      else enforceInactiveDropButtons(scope || document);
      return;
    }

    if (getDropState(scope) === "open") {
      if (promptVariantSelection(formShell)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      void submitSingleVariantForm(formShell);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    enforceInactiveDropButtons(scope || document);
  },
  true
);

document.addEventListener("click", (event) => {
  document.querySelectorAll("[data-variant-picker].is-open").forEach((picker) => {
    if (!picker.contains(event.target)) closeVariantPicker(picker);
  });

  const closeButton = event.target.closest("[data-menu-close]");
  if (closeButton) {
    closeMobileMenu(closeButton.closest("[data-mobile-menu]"), true);
    return;
  }

  const menuLink = event.target.closest("[data-mobile-menu] a");
  if (menuLink) closeMobileMenu(menuLink.closest("[data-mobile-menu]"));

  const openMenu = document.querySelector("[data-mobile-menu].is-open");
  if (
    openMenu &&
    !event.target.closest("[data-mobile-menu-panel]") &&
    !event.target.closest("[data-menu-toggle]")
  ) {
    closeMobileMenu(openMenu);
  }

  const thumbnail = event.target.closest("[data-gallery-thumb]");
  if (thumbnail) {
    const gallery = thumbnail.closest("[data-product-gallery]");
    setGalleryPosition(gallery, Number(thumbnail.dataset.galleryPosition || 0));
    return;
  }

  const galleryDirection = event.target.closest("[data-gallery-previous], [data-gallery-next]");
  if (galleryDirection) {
    const gallery = galleryDirection.closest("[data-product-gallery]");
    const currentPosition = Number(gallery?.dataset.galleryIndex || 0);
    setGalleryPosition(gallery, currentPosition + (galleryDirection.hasAttribute("data-gallery-next") ? 1 : -1));
    return;
  }

  const addButton = event.target.closest("[data-add-to-cart]");
  if (addButton) {
    const formShell = addButton.closest("[data-product-form-shell]");
    const scope = formShell?.closest("[data-product-scope], .featured-drop");
    if (formShell?.querySelector("[data-multi-variant-form]")) return;
    if (formShell && getDropState(scope) === "open" && promptVariantSelection(formShell)) {
      event.preventDefault();
      return;
    }
  }

  const multiQuantityControl = event.target.closest("[data-multi-quantity-minus], [data-multi-quantity-plus]");
  if (multiQuantityControl) {
    const stepper = multiQuantityControl.closest("[data-multi-quantity-stepper]");
    const input = stepper?.querySelector("[data-multi-quantity-input]");
    if (!input) return;
    const change = multiQuantityControl.hasAttribute("data-multi-quantity-plus") ? 1 : -1;
    setMultiVariantQuantity(input, Number(input.value || 0) + change);
    return;
  }

  const quantityControl = event.target.closest("[data-quantity-minus], [data-quantity-plus]");
  if (quantityControl) {
    const formShell = quantityControl.closest("[data-product-form-shell]");
    const input = formShell?.querySelector("[data-quantity-input]");
    if (!formShell || !input) return;
    const change = quantityControl.hasAttribute("data-quantity-plus") ? 1 : -1;
    setQuantity(formShell, Number(input.value || input.min || 1) + change);
    return;
  }

  const cartQuantityControl = event.target.closest("[data-cart-quantity-minus], [data-cart-quantity-plus]");
  if (cartQuantityControl) {
    const stepper = cartQuantityControl.closest("[data-cart-quantity-stepper]");
    const input = stepper?.querySelector("[data-cart-quantity-input]");
    const form = cartQuantityControl.closest("form");
    if (!stepper || !input || !form) return;

    const currentValue = Number(input.value || 0);
    const change = cartQuantityControl.hasAttribute("data-cart-quantity-plus") ? 1 : -1;
    const parsedMaximum = Number(input.max);
    const maximum = input.hasAttribute("max") && Number.isFinite(parsedMaximum) ? parsedMaximum : null;
    const nextValue = Math.max(0, maximum == null ? currentValue + change : Math.min(currentValue + change, maximum));
    input.value = String(nextValue);
    stepper.querySelectorAll("button").forEach((button) => { button.disabled = true; });

    const updateButton = form.querySelector('button[name="update"]');
    if (updateButton) form.requestSubmit(updateButton);
    else form.requestSubmit();
    return;
  }

  const stickyQuantityControl = event.target.closest("[data-sticky-quantity-minus], [data-sticky-quantity-plus]");
  if (stickyQuantityControl) {
    const scope = stickyQuantityControl.closest("[data-product-scope], .featured-drop");
    const formShell = scope?.querySelector("[data-product-form-shell]");
    const input = formShell?.querySelector("[data-quantity-input]");
    if (!formShell || !input) return;
    const change = stickyQuantityControl.hasAttribute("data-sticky-quantity-plus") ? 1 : -1;
    setQuantity(formShell, Number(input.value || input.min || 1) + change);
    return;
  }

  const stickySubmit = event.target.closest("[data-sticky-submit]");
  if (stickySubmit) {
    const scope = stickySubmit.closest("[data-product-scope], .featured-drop");
    const formShell = scope?.querySelector("[data-product-form-shell]");
    const form = formShell?.querySelector("form");
    const addButton = formShell?.querySelector("[data-add-to-cart]");
    if (formShell?.querySelector("[data-multi-variant-form]")) {
      if (formShell && getDropState(scope) === "open" && promptMultiVariantSelection(formShell)) return;
      if (form && addButton && !addButton.disabled) form.requestSubmit(addButton);
      return;
    }
    if (formShell && getDropState(scope) === "open" && promptVariantSelection(formShell)) return;
    if (form && addButton && !addButton.disabled) form.requestSubmit(addButton);
  }
});

document.addEventListener("change", (event) => {
  const variantSelect = event.target.closest("[data-variant-select]");
  if (variantSelect) updateVariantState(variantSelect);
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  const openMenu = document.querySelector("[data-mobile-menu].is-open");
  if (openMenu) closeMobileMenu(openMenu, true);
});

const initializeTheme = (root = document) => {
  initializeMobileMenus(root);
  initializeHeroCarousels(root);
  initializeDropNotes(root);
  initializeGiftingMascotAnimations(root);
  initializeProductGalleries(root);
  initializeQuantitySteppers(root);
  initializeMultiVariantForms(root);
  initializeMobilePurchaseBars(root);
  initializeVariantPickers(root);
  root.querySelectorAll("[data-variant-select]").forEach(updateVariantState);
  enforceInactiveDropButtons(root);
};

document.addEventListener("DOMContentLoaded", () => {
  initializeTheme();
  synchronizePrivacyContact();
  showExpiredDropCartNotice();
  pruneExpiredDropCart();
  scheduleDropStateRefresh();
});
document.addEventListener("shopify:section:load", (event) => initializeTheme(event.target));
